/**
 * ProjectTimeTracker - Floating timer badge when working on a project in Project Hub
 * Shows total accumulated project-level time + current session, with user summarization
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useProjectTimeContext } from '../../context/ProjectTimeContext';
import { useFloatingTimeTrackerBodyClass } from '../../hooks/useFloatingTimeTrackerBodyClass';
import { getProjectTimeEntries } from '../../utils/projectTimeTrackingService';
import { loadLeadTimeForProject } from '../../utils/projectLeadTimeLoad';
import { formatInvoiceMoney } from '../../utils/designerInvoiceGates';
import {
  isInvoiceVendorUserId,
  placeDesignerInvoiceHours,
  type DesignerInvoiceTimeGroup,
  type InvoicePerson,
} from '../../utils/designerInvoiceProjectTime';
import { useCRM } from '../../utils/CRMContext';
import { loadDesignerInvoiceTimeForProject } from '../../utils/designerInvoiceProjectTimeLoad';
import type { LeadTimeEntry, ProjectTimeEntry } from '../../types';
import type { ProjectTimeChartExtraMinutes } from './projectTimeChartData';
import { Clock, Plus, ChevronDown, LineChart, Info } from 'lucide-react';
import ProjectLogTimeDialog from './ProjectLogTimeDialog';
import ProjectUserTimeDialog from './ProjectUserTimeDialog';
import ProjectTimeActivityChart from './ProjectTimeActivityChart';
import './ProjectTimeTracker.css';

function formatTotalSeconds(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded}h`;
}

function isProjectLevel(e: ProjectTimeEntry): boolean {
  return !e.taskId;
}

export const ProjectTimeTracker: React.FC = () => {
  const { activeProjectId, activeProjectPhase, elapsedSeconds } = useProjectTimeContext();
  const { crmUsers, timberProjects, deals } = useCRM();
  const activeProject = timberProjects.find((project) => project.id === activeProjectId);
  const dealId = activeProject?.dealId || null;
  const leadId = (dealId && deals.find((deal) => deal.id === dealId)?.createdFrom) || null;
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId: string; userName: string } | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const [timeEntries, setTimeEntries] = useState<ProjectTimeEntry[]>([]);
  const [leadEntries, setLeadEntries] = useState<LeadTimeEntry[]>([]);
  const [invoiceGroups, setInvoiceGroups] = useState<DesignerInvoiceTimeGroup[]>([]);
  const [openInvoiceUsers, setOpenInvoiceUsers] = useState<Record<string, boolean>>({});
  const activeProjectIdRef = useRef(activeProjectId);
  activeProjectIdRef.current = activeProjectId;

  const fetchSummary = useCallback(async () => {
    if (!activeProjectId) return;
    const projectIdForFetch = activeProjectId;
    try {
      const [entries, leadRows] = await Promise.all([
        getProjectTimeEntries(projectIdForFetch).then((rows) => rows.filter(isProjectLevel)),
        loadLeadTimeForProject({ dealId, leadId }).catch(() => [] as LeadTimeEntry[]),
      ]);
      let groups: DesignerInvoiceTimeGroup[] = [];
      try {
        groups = await loadDesignerInvoiceTimeForProject(projectIdForFetch, dealId);
      } catch {
        groups = [];
      }
      if (activeProjectIdRef.current !== projectIdForFetch) return;
      setTimeEntries(entries);
      setLeadEntries(leadRows);
      setInvoiceGroups(groups);
    } catch {
      if (activeProjectIdRef.current !== projectIdForFetch) return;
      setTimeEntries([]);
      setLeadEntries([]);
      setInvoiceGroups([]);
    }
  }, [activeProjectId, dealId, leadId]);

  const invoicePeople = useMemo<InvoicePerson[]>(() => {
    const onProject = new Set([
      ...timeEntries.map(entry => entry.userId),
      ...leadEntries.map(entry => entry.userId),
    ]);
    return crmUsers
      .filter(user => user.isActive && (user.role === 'designer' || user.role === 'architect' || onProject.has(user.uid)))
      .map(user => ({
        userId: user.uid,
        userName: user.displayName || user.email || 'User',
        company: user.company,
        onProject: onProject.has(user.uid),
      }));
  }, [crmUsers, timeEntries, leadEntries]);

  const splitByUser = useMemo(() => {
    const map = new Map<string, { projectMinutes: number; leadMinutes: number }>();
    const touch = (userId: string) => {
      const current = map.get(userId);
      if (current) return current;
      const row = { projectMinutes: 0, leadMinutes: 0 };
      map.set(userId, row);
      return row;
    };
    timeEntries.forEach(entry => {
      touch(entry.userId).projectMinutes += entry.minutes;
    });
    leadEntries.forEach(entry => {
      touch(entry.userId).leadMinutes += entry.minutes;
    });
    return map;
  }, [timeEntries, leadEntries]);

  const placed = useMemo(() => {
    const trackedMap = new Map<string, { userId: string; userName: string; minutes: number }>();
    const add = (userId: string, userName: string, minutes: number) => {
      const current = trackedMap.get(userId);
      if (current) current.minutes += minutes;
      else trackedMap.set(userId, { userId, userName, minutes });
    };
    timeEntries.forEach(entry => add(entry.userId, entry.userName, entry.minutes));
    leadEntries.forEach(entry => add(entry.userId, entry.userName, entry.minutes));
    return placeDesignerInvoiceHours(Array.from(trackedMap.values()), invoiceGroups, invoicePeople);
  }, [timeEntries, leadEntries, invoiceGroups, invoicePeople]);

  useEffect(() => {
    if (!activeProjectId) {
      setTimeEntries([]);
      setLeadEntries([]);
      setInvoiceGroups([]);
      setOpenInvoiceUsers({});
      setShowActivityDetail(false);
      return;
    }
    fetchSummary();
    const id = setInterval(fetchSummary, 60000);
    return () => clearInterval(id);
  }, [activeProjectId, fetchSummary]);

  const chartExtraMinutes = useMemo<ProjectTimeChartExtraMinutes[]>(() => {
    const byVendor = new Map(placed.placements.map(row => [row.vendor, row]));
    const invoicePoints = invoiceGroups.flatMap(group => {
      const placement = byVendor.get(group.vendor);
      const userId = placement?.userId || group.vendor;
      const userName = placement?.userName || group.vendor;
      return group.chartPoints.map(point => ({
        userId,
        userName,
        minutes: point.minutes,
        date: point.date,
      }));
    });
    const leadPoints = leadEntries
      .filter(entry => entry.minutes > 0 && entry.startTime instanceof Date)
      .map(entry => ({
        userId: entry.userId,
        userName: entry.userName,
        minutes: entry.minutes,
        date: entry.startTime,
      }));
    return [...invoicePoints, ...leadPoints];
  }, [invoiceGroups, placed.placements, leadEntries]);

  const listedMinutes = placed.rows.reduce((sum, row) => sum + row.minutes, 0);
  const totalSeconds = listedMinutes * 60 + elapsedSeconds;

  useFloatingTimeTrackerBodyClass('project', Boolean(activeProjectId));

  if (!activeProjectId) return null;

  const phaseLabel =
    activeProjectPhase === 'estimating'
      ? 'Estimating'
      : activeProjectPhase === 'preconstruction'
        ? 'Precon'
        : 'Contract';

  return (
    <>
      <div className="project-time-tracker" role="status" aria-label={`Time on project: ${formatTotalSeconds(totalSeconds)}`}>
        {showSummary && (
          <div
            className={`project-time-tracker-summary${showActivityDetail ? ' project-time-tracker-summary--expanded' : ''}`}
          >
            <div className="project-time-tracker-summary-title">
              By user <span className="project-time-tracker-phase">{phaseLabel}</span>
            </div>
            {placed.rows.length > 0 ? (
              placed.rows.map(u => {
                const invoiceOnly = isInvoiceVendorUserId(u.userId);
                const open = Boolean(openInvoiceUsers[u.userId]);
                const hasInvoices = u.links.length > 0;
                return (
                  <div key={u.userId} className="project-time-tracker-user">
                    <div
                      className={`project-time-tracker-summary-row${invoiceOnly ? '' : ' clickable'}`}
                      onClick={() => {
                        if (!invoiceOnly) setSelectedUser({ userId: u.userId, userName: u.userName });
                      }}
                      role={invoiceOnly ? undefined : 'button'}
                      tabIndex={invoiceOnly ? undefined : 0}
                      onKeyDown={e => {
                        if (!invoiceOnly && e.key === 'Enter') {
                          setSelectedUser({ userId: u.userId, userName: u.userName });
                        }
                      }}
                    >
                      <div className="project-time-tracker-summary-who">
                        <span className="project-time-tracker-summary-name">
                          <span>{u.userName}</span>
                        </span>
                        {(splitByUser.get(u.userId)?.leadMinutes ?? 0) > 0 && (
                          <span className="project-time-tracker-split">
                            Project {formatMinutes(splitByUser.get(u.userId)?.projectMinutes ?? 0)}
                            {' · '}
                            Lead {formatMinutes(splitByUser.get(u.userId)?.leadMinutes ?? 0)}
                          </span>
                        )}
                      </div>
                      <span className="project-time-tracker-summary-time">
                        {hasInvoices && (
                          <button
                            type="button"
                            className="project-time-tracker-invoice-info"
                            aria-expanded={open}
                            aria-label={`Invoices included in ${u.userName}'s total`}
                            title="Includes hours imported from invoices"
                            onClick={e => {
                              e.stopPropagation();
                              setOpenInvoiceUsers(prev => ({ ...prev, [u.userId]: !prev[u.userId] }));
                            }}
                          >
                            <Info size={11} aria-hidden />
                          </button>
                        )}
                        <span>{formatMinutes(u.minutes)}</span>
                      </span>
                    </div>
                    {open && hasInvoices && (
                      <div className="project-time-tracker-invoice-detail">
                        {u.memberNames && u.memberNames.length > 0 && (
                          <span className="project-time-tracker-invoice-members">{u.memberNames.join(', ')}</span>
                        )}
                        {u.importedMinutes > 0 && (
                          <span className="project-time-tracker-invoice-badge">
                            {formatHours(u.importedMinutes / 60)} Imported from Invoices
                          </span>
                        )}
                        <div className="project-time-tracker-invoice-links">
                          {u.links.map(link => {
                            const detail = `${formatHours(link.hours)} · ${formatInvoiceMoney(link.amount)}`;
                            return link.purchaseRequisitionId ? (
                              <Link
                                key={link.key}
                                className="project-time-tracker-invoice-link"
                                to={`/purchasing?pr=${encodeURIComponent(link.purchaseRequisitionId)}`}
                                onClick={e => e.stopPropagation()}
                              >
                                {link.label} · {detail}
                              </Link>
                            ) : (
                              <span key={link.key} className="project-time-tracker-invoice-plain">
                                {link.label} · {detail}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="project-time-tracker-summary-empty">No time logged yet</div>
            )}
            {placed.rows.length > 0 && (
              <div className="project-time-tracker-summary-total">
                <span>Total</span>
                <span>{formatMinutes(listedMinutes)}</span>
              </div>
            )}
            <button
              type="button"
              className="project-time-tracker-detail-btn"
              onClick={e => {
                e.stopPropagation();
                setShowActivityDetail(d => !d);
              }}
              aria-expanded={showActivityDetail}
              title={showActivityDetail ? 'Hide activity chart' : 'Show activity over time'}
            >
              <LineChart size={14} aria-hidden />
              <span>Activity detail</span>
              <ChevronDown size={12} className={`project-time-tracker-chevron ${showActivityDetail ? 'open' : ''}`} aria-hidden />
            </button>
            {showActivityDetail && (
              <ProjectTimeActivityChart entries={timeEntries} extraMinutes={chartExtraMinutes} />
            )}
          </div>
        )}
        <div className="project-time-tracker-row">
          <div
            className="project-time-tracker-main"
            onClick={() => setShowSummary(s => !s)}
            title="Click for breakdown by user"
          >
            <div className="project-time-tracker-timer">
              <Clock size={14} aria-hidden />
              <span>{formatTotalSeconds(totalSeconds)}</span>
            </div>
            <ChevronDown size={12} className={`project-time-tracker-chevron ${showSummary ? 'open' : ''}`} aria-hidden />
          </div>
          <button
            type="button"
            className="project-time-tracker-log-btn"
            onClick={() => setShowLogDialog(true)}
            title="Log time manually"
            aria-label="Log time manually"
          >
            <Plus size={14} aria-hidden />
          </button>
        </div>
      </div>

      {showLogDialog && (
        <ProjectLogTimeDialog
          projectId={activeProjectId}
          phase={activeProjectPhase}
          onClose={() => setShowLogDialog(false)}
          onSaved={fetchSummary}
        />
      )}

      {selectedUser && (
        <ProjectUserTimeDialog
          projectId={activeProjectId}
          userId={selectedUser.userId}
          userName={selectedUser.userName}
          onClose={() => setSelectedUser(null)}
          onSaved={fetchSummary}
        />
      )}
    </>
  );
};
